import { useEffect, useRef, useState, type CSSProperties } from "react";

/*
 * The MineOptics mark: the bench contours of a pit seen from above, each ring
 * stepping toward the ore at the bottom. It also reads as a lens.
 *
 * Rings take the current text colour. The centre takes --mark-dot, which each
 * app sets to its accent colour (see brand.css).
 */

interface Props {
  className?: string;
}

const DOT = "var(--mark-dot, currentColor)";

export function LogoMark({ className }: Props) {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" className={`mo-mark ${className ?? ""}`}>
      <circle cx="16" cy="16" r="13.2" stroke="currentColor" strokeWidth="1.6" />
      <ellipse className="mo-mark__inner" cx="16.8" cy="16.6" rx="8" ry="7.4" stroke="currentColor" strokeWidth="1.6" opacity="0.65" />
      <circle className="mo-mark__dot" cx="17.6" cy="17.2" r="3" fill={DOT} />
    </svg>
  );
}

/** Mark and wordmark. The rings pull into focus when the lockup is hovered. */
export function Logo({ className }: Props) {
  return (
    <span className={`mo-logo ${className ?? ""}`}>
      <LogoMark className="mo-logo__mark" />
      <span className="mo-logo__word">MineOptics</span>
    </span>
  );
}

/** Loading indicator: the inner ring and the ore orbit inside the rim. */
export function MarkSpinner({ className, label = "Loading" }: Props & { label?: string }) {
  return (
    <svg viewBox="0 0 32 32" fill="none" role="status" aria-label={label} className={`mo-spinner ${className ?? ""}`}>
      <circle cx="16" cy="16" r="13.2" stroke="currentColor" strokeWidth="1.6" opacity="0.3" />
      <g className="mo-spinner__orbit">
        <ellipse cx="17.6" cy="17.2" rx="8" ry="7.4" stroke="currentColor" strokeWidth="1.6" opacity="0.7" />
        <circle cx="19.6" cy="18.6" r="3" fill={DOT} />
      </g>
    </svg>
  );
}

interface MotifProps extends Props {
  /** Number of rings between the rim and the centre. */
  rings?: number;
  /** Draw the rings in when the motif scrolls into view (default) or show them at once. */
  draw?: boolean;
  /** Stroke width in viewBox units (the box is 200 wide): about 0.3 for a 40rem motif, 3 for a 6rem one. */
  strokeWidth?: number;
  /** Fill the centre. Turn off where the motif sits behind text. */
  dot?: boolean;
  /** Use --mo-draw and --mo-dot-delay to change the timing of the draw. */
  style?: CSSProperties;
}

/**
 * The mark at large scale, for backgrounds: more benches, hairline strokes.
 * Size and colour come from the class name (`text-fg/20`, `h-[40rem]`, ...).
 */
export function MarkMotif({ className, rings = 6, draw = true, dot = true, strokeWidth = 0.35, style }: MotifProps) {
  const ref = useRef<SVGSVGElement>(null);
  const [drawn, setDrawn] = useState(!draw);

  useEffect(() => {
    const element = ref.current;
    if (!element || !draw) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setDrawn(true);
        observer.disconnect();
      },
      { threshold: 0.2 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [draw]);

  return (
    <svg
      ref={ref}
      viewBox="0 0 200 200"
      fill="none"
      aria-hidden="true"
      strokeWidth={strokeWidth}
      style={style}
      className={`mo-motif ${drawn ? "is-drawn" : ""} ${className ?? ""}`}
    >
      <circle className="mo-motif__ring" cx="100" cy="100" r="98" pathLength={1} stroke="currentColor" />
      <g className="mo-motif__orbit">
        {Array.from({ length: rings }, (_, index) => {
          const step = (index + 1) / (rings + 1);
          const radius = 98 - step * 80;
          return (
            <ellipse
              key={index}
              className="mo-motif__ring"
              style={{ transitionDelay: `${(index + 1) * 110}ms` }}
              cx={100 + step * 12}
              cy={100 + step * 9}
              rx={radius}
              ry={radius * 0.93}
              pathLength={1}
              stroke="currentColor"
            />
          );
        })}
        {dot && (
          <circle className="mo-motif__dot" cx="112" cy="109" r={(98 - (rings / (rings + 1)) * 80) * 0.38} fill={DOT} />
        )}
      </g>
    </svg>
  );
}
